/**
 * Shared validation functions for truss accordion components
 */

export const validateSizeFeet = (value: number): string | undefined => {
  if (typeof value !== 'number' || isNaN(value)) {
    return 'Truss size must be a valid number';
  }
  if (value < 1) {
    return 'Truss size must be at least 1 foot';
  }
  if (value > 100) {
    return 'Truss size must be 100 feet or less';
  }
  return undefined;
};

export const MAX_TRUSS_SIZE_FEET = 100;

export const validateTotalSize = (sizeFeet: number, sizeInches: number): string | undefined => {
  if (sizeFeet === MAX_TRUSS_SIZE_FEET && sizeInches > 0) {
    return 'Maximum truss size is 100 feet. Inches must be 0 when feet is 100.';
  }
  return undefined;
};

export const validateQuantity = (value: number): string | undefined => {
  if (typeof value !== 'number' || isNaN(value) || value < 1) {
    return 'Quantity must be at least 1';
  }
  if (value > 999) {
    return 'Quantity must be 999 or less';
  }
  return undefined;
};

export const validateOverhangSize = (value: number): string | undefined => {
  if (typeof value !== 'number' || isNaN(value) || value < 0) {
    return 'Overhang cannot be negative';
  }
  if (value > 24) {
    return 'Overhang must be 24 inches or less';
  }
  return undefined;
};

/**
 * Type guard to check if a value is a valid number
 */
export const isNumericValue = (value: unknown): value is number => {
  return typeof value === 'number' && !isNaN(value);
};

/**
 * Rejects scientific notation in input strings
 */
export const hasScientificNotation = (input: string): boolean => {
  return /[eE]/.test(input);
};

/**
 * Header truss width must equal bay spacing × 2 (feet and inches combined).
 */
export function validateHeaderTrussWidth(
  sizeFeet: number,
  sizeInches: number,
  baySpacing: number
): string | undefined {
  if (!baySpacing || baySpacing <= 0 || !sizeFeet) {
    return undefined;
  }

  const expectedWidth = baySpacing * 2;
  const providedWidthFeet = sizeFeet + sizeInches / 12;

  if (Math.abs(providedWidthFeet - expectedWidth) > 0.0001) {
    const received = sizeInches > 0 ? `${sizeFeet}ft ${sizeInches}"` : `${sizeFeet}ft`;
    return (
      `Header truss width must be double the bay spacing. ` +
      `Bay Spacing: ${baySpacing}ft → Expected Header Width: ${expectedWidth}ft, ` +
      `but received: ${received}`
    );
  }

  return undefined;
}
